import type { PlaceholderValues } from "@homeinstead/schemas";
import type { FormStep, MultiSelectOption, RadioOption } from "./form-types";

function buildPlaceholderKey(fieldName: string): string {
  return `lfb → ${fieldName}`;
}

/**
 * Builds a PlaceholderValues map from persisted form answers so later steps
 * can reference previous answers via the dynamicPlaceholder mark.
 *
 * radioInput: the resolved display label for the selected option is stored
 * under the radio's fieldName key. The lookup mirrors how RadioInput derives
 * the submitted value: option.value ?? option.label.
 *
 * multiSelectInput: each selected option is persisted as
 * "fieldName.optionValue" = "on". The display labels of all selected options
 * are joined with ", " (in the order they appear in the Sanity schema) and
 * stored under the fieldName key.
 *
 * Example: careFor = "parent" → { "lfb → careFor": "My parent(s)" }
 * Example: careType selections → { "lfb → careType": "Memory care, Everyday tasks" }
 *
 * Keys follow the "lfb → fieldName" namespace convention, matching the
 * "office → phone" pattern used by document-context placeholders.
 */
function buildFormAnswerPlaceholders(
  steps: FormStep[],
  persistedFieldValues: Record<string, string>,
): PlaceholderValues {
  const result: PlaceholderValues = {};

  for (const step of steps) {
    if (step._type !== "step" || !step.inputs) {
      continue;
    }

    for (const input of step.inputs) {
      if (input._type === "radioInput") {
        if (!input.fieldName) {
          continue;
        }

        const persistedValue = persistedFieldValues[input.fieldName];
        if (!persistedValue) {
          continue;
        }

        const matchedOption = input.options.find((option: RadioOption) => {
          const submittedValue = option.value ?? option.label;
          return submittedValue === persistedValue;
        });

        if (matchedOption?.label) {
          result[buildPlaceholderKey(input.fieldName)] = matchedOption.label;
        }
      } else if (input._type === "multiSelectInput") {
        if (!input.fieldName) {
          continue;
        }
        if (!Array.isArray(input.options)) {
          continue;
        }

        const selectedLabels = input.options
          .filter((option: MultiSelectOption) => {
            const optionValue = option.value ?? option.label ?? option._key;
            return (
              persistedFieldValues[`${input.fieldName}.${optionValue}`] === "on"
            );
          })
          .map((option: MultiSelectOption) => option.label)
          .filter(Boolean);

        if (selectedLabels.length > 0) {
          result[buildPlaceholderKey(input.fieldName)] =
            selectedLabels.join(", ");
        }
      }
    }
  }

  return result;
}

export { buildFormAnswerPlaceholders };
